import { useId, useState } from 'react';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@design-system';
import { formatByCountry, stripDigits, validateForCountry } from '@client/util/clerkAuthHelpers';

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | 'date' | 'month';
  autoComplete?: string;
  hint?: string;
  multiline?: boolean;
  maxLength?: number;
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  hint,
  multiline,
  maxLength = 200,
}: TextFieldProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      {multiline ? (
        <Textarea id={id} rows={3} maxLength={2000} className="bg-white" value={value} onChange={event => onChange(event.target.value)} />
      ) : (
        <Input
          id={id}
          type={type}
          autoComplete={autoComplete}
          maxLength={maxLength}
          className="bg-white"
          value={value}
          onChange={event => onChange(event.target.value)}
        />
      )}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

interface PhoneFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
}

function usPhoneDigits(value: string): string {
  const digits = stripDigits(value);
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/** US phone entry, using the same formatting and checks as signup and invite. */
export function PhoneField({ label, value, onChange, autoComplete = 'tel' }: PhoneFieldProps) {
  const id = useId();
  const digits = usPhoneDigits(value);
  const message = digits.length > 0 ? validateForCountry(digits, '+1') : null;
  const [touched, setTouched] = useState(false);
  const showError = touched && !!message;

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete={autoComplete}
        placeholder="(555) 555-5555"
        className="bg-white"
        value={formatByCountry(digits, '+1')}
        aria-invalid={showError || undefined}
        onBlur={() => setTouched(true)}
        onChange={event => onChange(formatByCountry(usPhoneDigits(event.target.value), '+1'))}
      />
      {showError && (
        <p role="alert" className="text-destructive text-sm">
          {message}
        </p>
      )}
    </div>
  );
}

interface SelectFieldProps<T extends string> {
  label: string;
  value: T | '';
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}

export function SelectField<T extends string>({ label, value, onChange, options }: SelectFieldProps<T>) {
  const id = useId();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={next => onChange(next as T)}>
        <SelectTrigger id={id} className="w-full bg-white">
          <SelectValue placeholder="Choose one" />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export const YES_NO_OPTIONS: { value: Exclude<ConsumerYesNo, ''>; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];
